import React from 'react';
import { Alert, Button, Modal, Space, Typography } from 'antd';

import { DevPlanLaunchSelection } from '../types';

type Props = {
  selection: DevPlanLaunchSelection | null;
  onOpenExisting: () => void;
  onStay: () => void;
};

const blueprintLabel = (
  blueprint: DevPlanLaunchSelection['requested_blueprint'],
) => blueprint?.name || blueprint?.slug || 'Unknown scenario';

/**
 * An explicit scenario click can be overridden by the node's one-unfinished-
 * solo-session invariant. Keep the user on the plan until they choose whether
 * to enter that existing room, and make the override visible instead of
 * silently navigating to a different scenario.
 *
 * Product terminology: user-facing copy says "single player session"; "solo"
 * is retained only in internal component, action, and API identifiers.
 */
const ExistingSoloSessionModal: React.FC<Props> = ({
  selection,
  onOpenExisting,
  onStay,
}) => (
  <Modal
    title="Another scenario is already in progress"
    open={!!selection}
    onCancel={onStay}
    mask={{ closable: false }}
    footer={[
      <Button key="stay" onClick={onStay}>
        Stay in development plan
      </Button>,
      <Button key="open" type="primary" onClick={onOpenExisting}>
        Open existing session
      </Button>,
    ]}
  >
    {selection && (
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          title="Only one unfinished single player session can be open for this node."
          description="Your selected scenario was not started. Finish or stop the existing session before starting another scenario in this node."
        />
        <div>
          <Typography.Text type="secondary">You selected</Typography.Text>
          <div>
            <Typography.Text strong>
              {blueprintLabel(selection.requested_blueprint)}
            </Typography.Text>
          </div>
        </div>
        <div>
          <Typography.Text type="secondary">
            Currently in progress
          </Typography.Text>
          <div>
            <Typography.Text strong>
              {blueprintLabel(selection.active_blueprint)}
            </Typography.Text>
          </div>
        </div>
      </Space>
    )}
  </Modal>
);

export default ExistingSoloSessionModal;
